<template>
  <el-dialog
    v-model="visible"
    align-center
    :width="dialogWidth"
    :show-close="false"
    :lock-scroll="true"
    :close-on-click-modal="true"
    class="pc-activity-modal-dialog"
    @closed="handleDialogClosed"
  >
    <div v-if="currentModal" class="pc-activity-modal">
      <img
        v-if="currentModal.content"
        class="pc-activity-modal__image"
        :class="{ 'pc-activity-modal__image--disabled': isNoJumpModal(currentModal) }"
        :src="currentModal.content"
        :alt="currentModal.title || '活动弹窗'"
        @click="handleModalClick"
        @load="finishImageLoading"
        @error="finishImageLoading"
      />

      <button
        v-if="imageLoaded"
        class="pc-activity-modal__disappear"
        type="button"
        @click.stop="hideCurrentModal"
      >
        {{ getDisappearText(currentModal.disappearType) }}
      </button>

      <button
        v-if="imageLoaded"
        class="pc-activity-modal__close"
        type="button"
        aria-label="关闭"
        @click.stop="closeCurrentModal"
      >
        <img class="pc-activity-modal__close-icon" :src="activityCloseIcon" alt="" />
      </button>
    </div>
  </el-dialog>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import * as UserApi from '@/api/user.ts'
import { reportEvent } from '@/utils/auth.ts'
import activityCloseIcon from '@/assets/images/activities/activity-close.svg'
import { ACTIVITY_LOCATION, getActivityPageNamePrefix, normalizeActivityLocation } from '@/utils/activityLocation'

defineOptions({ name: 'PcActivityModal' })

const props = defineProps({
  location: {
    type: String,
    default: ACTIVITY_LOCATION.HOME
  },
  deferLoginUntilFinished: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['finished'])

const router = useRouter()
const visible = ref(false)
const modalList = ref([])
const imageLoaded = ref(true)
const closingModal = ref(false)
const hidingModal = ref(false)
const requestId = ref(0)
const lastShownModalId = ref('')
const modalFlowFinished = ref(false)
let imageLoadingTimer = null
let originalOpenLogin = null
let wrappedOpenLogin = null
let pendingLoginArgs = null

const STORAGE_KEY = 'ydppt_pc_activity_modal_state'
const OPEN_COUPON_CENTER_EVENT = 'open-coupon-center'
const COUPON_CENTER_LINK_PATHS = new Set(['/couponCenter', '/m/coupon-center'])

const currentModal = computed(() => modalList.value[0] || null)

const dialogWidth = 'auto'

const pageNamePrefix = computed(() => getActivityPageNamePrefix(props.location))
const requestLocation = computed(() => normalizeActivityLocation(props.location))

const getTodayKey = () => {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getModalId = (modal) => String(modal?.id || modal?.title || modal?.content || '')

const readState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch (error) {
    return {}
  }
}

const writeState = (state) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

const getModalRecord = (state, modalId) => {
  if (!state[modalId]) {
    state[modalId] = {
      totalShows: 0,
      dailyShows: {},
      hiddenType: ''
    }
  }
  return state[modalId]
}

const isModalHidden = (record) => {
  if (record.hiddenType === 'FOREVER') return true
  return record.hiddenType === 'TODAY' && record.hiddenDate === getTodayKey()
}

const isWithinShowLimit = (modal) => {
  const modalId = getModalId(modal)
  if (!modalId) return false

  const state = readState()
  const record = getModalRecord(state, modalId)
  if (isModalHidden(record)) return false

  const todayKey = getTodayKey()
  const dailyLimit = Number(modal.dailyShowLimit || 0)
  const totalLimit = Number(modal.totalShowLimit || 0)
  const dailyShows = Number(record.dailyShows?.[todayKey] || 0)
  const totalShows = Number(record.totalShows || 0)

  if (dailyLimit > 0 && dailyShows >= dailyLimit) return false
  if (totalLimit > 0 && totalShows >= totalLimit) return false
  return true
}

const recordModalShow = (modal) => {
  const modalId = getModalId(modal)
  if (!modalId || lastShownModalId.value === modalId) return

  const todayKey = getTodayKey()
  const state = readState()
  const record = getModalRecord(state, modalId)
  record.totalShows = Number(record.totalShows || 0) + 1
  record.dailyShows = {
    ...(record.dailyShows || {}),
    [todayKey]: Number(record.dailyShows?.[todayKey] || 0) + 1
  }
  writeState(state)
  lastShownModalId.value = modalId

  if (modal.title) {
    reportEvent('window', {
      window_name: pageNamePrefix.value + modal.title
    })
  }
}

const markModalHidden = (modal) => {
  const modalId = getModalId(modal)
  if (!modalId) return

  const state = readState()
  const record = getModalRecord(state, modalId)
  const hiddenType = modal.disappearType === 'FOREVER' ? 'FOREVER' : 'TODAY'
  record.hiddenType = hiddenType
  record.hiddenDate = hiddenType === 'TODAY' ? getTodayKey() : ''
  writeState(state)
}

const clearImageLoadingTimer = () => {
  if (imageLoadingTimer) {
    clearTimeout(imageLoadingTimer)
    imageLoadingTimer = null
  }
}

const finishImageLoading = () => {
  clearImageLoadingTimer()
  imageLoaded.value = true
}

const getDisappearText = (disappearType) => {
  if (disappearType === 'FOREVER') return '永久不再弹出'
  return '今日不再弹出'
}

const hasLoginToken = () => Boolean(localStorage.getItem('ydppt_token'))

const shouldDeferLogin = () => {
  return props.deferLoginUntilFinished && !modalFlowFinished.value && !hasLoginToken()
}

const flushPendingLogin = () => {
  if (!pendingLoginArgs) return

  const args = pendingLoginArgs
  pendingLoginArgs = null
  if (hasLoginToken()) return

  const openLogin = originalOpenLogin || (window.openLogin !== wrappedOpenLogin ? window.openLogin : null)
  if (typeof openLogin === 'function') {
    openLogin(...args)
  }
}

const finishModalFlow = () => {
  if (modalFlowFinished.value) return

  modalFlowFinished.value = true
  emit('finished')
  flushPendingLogin()
}

const installLoginInterceptor = () => {
  if (!props.deferLoginUntilFinished || typeof window === 'undefined') return

  originalOpenLogin = window.openLogin
  wrappedOpenLogin = (...args) => {
    const shouldOpenLogin = args[0] !== false
    if (shouldOpenLogin && shouldDeferLogin()) {
      pendingLoginArgs = args
      return
    }

    if (typeof originalOpenLogin === 'function') {
      originalOpenLogin(...args)
    }
  }
  window.openLogin = wrappedOpenLogin
}

const restoreLoginInterceptor = () => {
  if (typeof window === 'undefined') return
  if (window.openLogin === wrappedOpenLogin) {
    window.openLogin = originalOpenLogin
  }
}

const loadModalList = async () => {
  const nextRequestId = requestId.value + 1
  requestId.value = nextRequestId

  try {
    const res = await UserApi.getModalListByLocation({
      params: {
        location: requestLocation.value
      }
    })
    if (nextRequestId !== requestId.value) return
    if (res?.code !== 'SUCCESS') {
      finishModalFlow()
      return
    }

    const list = Array.isArray(res.data) ? res.data : []
    modalList.value = list.filter((modal) => modal?.content && isWithinShowLimit(modal))
    if (modalList.value.length === 0) {
      finishModalFlow()
    }
  } catch (error) {
    console.warn('Failed to fetch PC activity modal:', error)
    finishModalFlow()
  }
}

const showNextModal = () => {
  modalList.value = modalList.value.slice(1)
  if (modalList.value.length === 0) {
    visible.value = false
  }
}

const closeCurrentModal = () => {
  if (!currentModal.value || closingModal.value) return
  closingModal.value = true
  showNextModal()
  setTimeout(() => {
    closingModal.value = false
  }, 200)
}

const hideCurrentModal = async () => {
  const modal = currentModal.value
  if (!modal || hidingModal.value) return

  hidingModal.value = true
  markModalHidden(modal)
  if (modal.title) {
    reportEvent('app_clicks', {
      ck_module: pageNamePrefix.value + '关闭_' + modal.title
    })
  }

  try {
    if (modal.id) {
      await UserApi.hiddenActivity({ params: { id: modal.id } })
    }
  } catch (error) {
    console.warn('Failed to hide PC activity modal:', error)
  } finally {
    hidingModal.value = false
    showNextModal()
  }
}

const normalizeUrl = (url) => {
  if (!url) return ''
  if (/^https?:\/\//.test(url)) return url
  return url.startsWith('/') ? url : `/${url}`
}

const isCouponCenterModal = (modal) => {
  const linkUrl = String(modal?.linkUrl || '').trim()
  const path = linkUrl.split(/[?#]/)[0].replace(/\/+$/, '')
  return COUPON_CENTER_LINK_PATHS.has(path)
}

const isNoJumpModal = (modal) => {
  if (!modal) return true

  const linkType = String(modal.linkType || '').toUpperCase()
  const noJumpTypes = ['NONE', 'NO_JUMP', 'NO_LINK', 'NOT_JUMP', 'DISABLED']
  if (noJumpTypes.includes(linkType)) return true

  return !modal.linkUrl && !modal.appPageId
}

const handleModalClick = () => {
  const modal = currentModal.value
  if (!modal) return

  if (modal.title) {
    reportEvent('app_clicks', {
      ck_module: pageNamePrefix.value + '点击_' + modal.title
    })
  }

  if (isCouponCenterModal(modal)) {
    closeCurrentModal()
    window.dispatchEvent(new Event(OPEN_COUPON_CENTER_EVENT))
    return
  }

  if (isNoJumpModal(modal)) {
    closeCurrentModal()
    return
  }

  if (modal.linkType === 'APP_PAGE' && modal.appPageId) {
    window.open(`/article/detail?id=${modal.appPageId}`, '_blank')
    return
  }

  const url = normalizeUrl(modal.linkUrl)
  if (!url) {
    closeCurrentModal()
    return
  }

  if (/^https?:\/\//.test(url)) {
    window.open(url, '_blank')
    return
  }

  router.push(url)
}

const handleDialogClosed = () => {
  if (modalList.value.length > 0 && currentModal.value) {
    modalList.value = modalList.value.slice(1)
    if (modalList.value.length > 0) return
  }

  if (modalList.value.length === 0) {
    finishModalFlow()
  }
}

watch(currentModal, (modal, prevModal) => {
  clearImageLoadingTimer()
  if (!modal) {
    visible.value = false
    imageLoaded.value = true
    return
  }

  visible.value = true
  imageLoaded.value = !modal.content
  if (modal.content && modal.content !== prevModal?.content) {
    imageLoadingTimer = setTimeout(() => {
      imageLoaded.value = true
      imageLoadingTimer = null
    }, 3000)
  }
  recordModalShow(modal)
})

onMounted(() => {
  installLoginInterceptor()
  loadModalList()
})

onUnmounted(() => {
  clearImageLoadingTimer()
  requestId.value += 1
  restoreLoginInterceptor()
})

defineExpose({
  reload: loadModalList,
  close: closeCurrentModal
})
</script>

<style lang="scss" scoped>
:global(.el-dialog.pc-activity-modal-dialog) {
  width: fit-content;
  max-width: min(72vw, 720px);
  overflow: visible;
  padding: 0;
  border-radius: 16px;
  background: transparent;
  box-shadow: none;
}

:global(.el-dialog.pc-activity-modal-dialog .el-dialog__header) {
  display: none;
}

:global(.el-dialog.pc-activity-modal-dialog .el-dialog__body) {
  padding: 0;
}

.pc-activity-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: auto;
  max-width: 72vw;
  overflow: visible;
}

.pc-activity-modal__image {
  display: block;
  width: auto;
  height: auto;
  max-width: min(72vw, 720px);
  max-height: 70vh;
  border-radius: 16px;
  object-fit: contain;
  cursor: pointer;
}

.pc-activity-modal__image--disabled {
  cursor: pointer;
}

.pc-activity-modal__close {
  margin-top: 12px;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.pc-activity-modal__close-icon {
  display: block;
  width: 30px;
  height: 30px;
}

.pc-activity-modal__disappear {
  margin-top: 12px;
  padding: 0;
  border: none;
  background: transparent;
  color: #ffffff;
  font-family: "AlibabaPuHuiTi-Regular", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-decoration: underline;
  cursor: pointer;
}
</style>
